import { NO_ERP_INTEGRATION_MESSAGE, type CostingTransactionLogDto, type PostingLogStatusFilter } from '@/services/postingLogApi';
import { getApiErrorStatus } from '@/services/reportPaging';
import type { PostingLineDisplayStatus, PostingStatus, PostingLogRow, PostingLogTimesheet, PostingLogFilters, PostingLogQuery, SortKey, SortDir } from '@/utils/PostingLog/types';
import { resolvePostingLogWeekEnding } from './weekEnding';

// ─── API mapping ──────────────────────────────────────────────────────────────

export const mapPostingLogRow = (dto: CostingTransactionLogDto): PostingLogRow => ({
  id: String(dto.id),
  logId: dto.id,
  timesheetId: String(dto.timesheetId),
  weekEnding: dto.weekEnding ? new Date(dto.weekEnding) : undefined,
  employeeId: dto.employeeId,
  employeeName: dto.employeeName ?? `Employee ${dto.employeeId}`,
  transactionDate: dto.transactionDate ? new Date(dto.transactionDate) : undefined,
  projectCode: dto.projectCode ?? '',
  projectName: dto.projectName ?? dto.projectCode ?? '—',
  costCentreCode: dto.costCentreCode ?? '',
  costCentreName: dto.costCentreName ?? dto.costCentreCode ?? '—',
  quantity: dto.quantity ?? 0,
  price: dto.price ?? 0,
  upliftValue: dto.upliftValue ?? 0,
  status: dto.success ? 'Posted' : 'Failed',
  erpRef: dto.success ? dto.reference ?? undefined : undefined,
  errorMessage: dto.errorMessage ?? undefined,
});

export const groupPostingLogRows = (
  rows: PostingLogRow[],
  weekStartDay: Parameters<typeof resolvePostingLogWeekEnding>[1],
): PostingLogTimesheet[] => {
  const groups = new Map<string, PostingLogRow[]>();
  for (const row of rows) {
    const lines = groups.get(row.timesheetId) ?? [];
    lines.push(row);
    groups.set(row.timesheetId, lines);
  }
  return [...groups.values()].map((lines) => {
    const erpRefs = getDistinctErpRefs(lines);
    return {
      timesheetId: lines[0].timesheetId,
      employeeName: lines[0].employeeName,
      weekEnding: resolvePostingLogWeekEnding(lines, weekStartDay) ?? undefined,
      erpRef: resolveTimesheetErpRef(erpRefs),
      erpRefs,
      status: lines.some((line) => line.status === 'Failed') ? 'Failed' : 'Posted',
      lineIds: lines.map((line) => line.id),
      logIds: lines.map((line) => line.logId),
    };
  });
};

/**
 * Rolls up the per-line ERP references into a single value for the timesheet header row:
 * blank when no line has posted yet, the shared reference when every posted line shares one,
 * or "Multiple" when the posted lines carry more than one header reference.
 */
const getDistinctErpRefs = (lines: PostingLogRow[]): string[] =>
  [...new Set(lines.map((line) => line.erpRef).filter((ref): ref is string => !!ref))];

const resolveTimesheetErpRef = (refs: string[]): string => {
  if (refs.length === 0) return '';
  if (refs.length === 1) return refs[0];
  return 'Multiple';
};

/**
 * Resolves the status filters to the single `status` the API accepts.
 * `undefined` means no status filter; `null` means no posting line can match
 * (e.g. a Posted-only selection combined with `failedOnly`).
 */
export const resolveStatusFilter = (filters: PostingLogFilters): PostingLogStatusFilter | undefined | null => {
  let allowed: PostingStatus[] = filters.statuses?.length ? filters.statuses : ['Posted', 'Failed'];
  if (filters.failedOnly) allowed = allowed.filter((s) => s === 'Failed');
  const posted = allowed.includes('Posted');
  const failed = allowed.includes('Failed');
  if (posted && failed) return undefined;
  if (posted) return 'posted';
  if (failed) return 'failed';
  return null;
};

export const describeRetryError = (error: unknown): string => {
  switch (getApiErrorStatus(error)) {
    case 503:
      return NO_ERP_INTEGRATION_MESSAGE;
    case 403:
      return "You don't have permission to retry postings.";
    case 404:
      return 'This posting line could not be found.';
    default: {
      const errors = (error as { data?: { errors?: unknown } })?.data?.errors;
      if (Array.isArray(errors) && typeof errors[0] === 'string') return errors[0];
      return 'The ERP returned an error. Please try again.';
    }
  }
};

export const toPostingLogQuery = (
  filters: PostingLogFilters,
  search: string,
  sortKey: SortKey,
  sortDir: SortDir,
): PostingLogQuery => ({
  employeeIds: filters.employeeIds,
  projectCodes: filters.projectIds,
  costCentreCodes: filters.costCentreIds,
  departments: filters.departments,
  status: resolveStatusFilter(filters) ?? undefined,
  dateFrom: filters.dateFrom,
  dateTo: filters.dateTo,
  search: search || undefined,
  sortBy: sortKey,
  sortDir,
});

/** A failed line with no error of its own wasn't at fault — it wasn't posted because its batch was rejected. */
export const getLineDisplayStatus = (row: Pick<PostingLogRow, 'status' | 'errorMessage'>): PostingLineDisplayStatus =>
  row.status === 'Failed' && !row.errorMessage ? 'Not posted' : row.status;
